import { createFileRoute, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { handleGetGeometryGeometriesGeometryIdGetOptions } from '@/api/generated/@tanstack/react-query.gen';
import { useAuth } from '@/hooks/useAuth';
import { useNoIndex } from '@/hooks/useNoIndex';
import { useRecordRecentView } from '@/hooks/useRecordRecentView';
import { CenteredSpinner } from '@/components/CenteredSpinner';
import {
  AccessDeniedScreen,
  ShareLoadErrorScreen,
} from '@/components/share/AccessDeniedScreen';
import {
  ReadOnlyBanner,
  OpenGeometryInAppBanner,
} from '@/components/share/ReadOnlyBanner';
import { UUID_REGEX } from '@/lib/uuid';
import {
  canOpenInApp,
  isAccessDeniedError,
  readOnlyBannerRole,
} from '@/utils/shareAccess';
import { WorkspaceReadOnlyProvider } from '@/features/workspace-readonly';
import { MeshGenWorkspace } from '@/routes/_app/meshgen/-components/MeshGenWorkspace';

const uuidParam = z.string().regex(UUID_REGEX).optional().catch(undefined);

// Mirrors `/_app/meshgen/$uuid`'s search schema, minus `study`/`dp` — a public
// viewer never lands inside a SimOps study-chrome mode, so those two params
// have no meaning here.
const shareGeometrySearchSchema = z.object({
  mesh: uuidParam,
  geometry: uuidParam,
});

/**
 * Read-only public geometry viewer (ENG-1071) — renders the REAL MeshGen
 * workspace (`MeshGenWorkspace`, the same component `/meshgen/$uuid` mounts)
 * wrapped in `WorkspaceReadOnlyProvider`, instead of the old standalone
 * coarse-CAD-only preview (ENG-1067). This gives an anonymous visitor the
 * generated mesh, working view toggles, and the full panel layout — with
 * every edit/settings control grayed out and every mutation hook guarded
 * (see `src/features/workspace-readonly/` and the per-component gating it
 * documents in `-components/AGENTS.md` and `features/mesh-authoring/AGENTS.md`).
 *
 * Sits OUTSIDE the `_app` route tree, so there is no auth guard here — this
 * must render correctly for a fully signed-out visitor. `useMeshGenWorkspace`
 * and its sub-hooks are made auth-tolerant for exactly this route (see
 * `useMeshGenWorkspace.ts`'s read-only branch).
 */
function GeometryShareRoute() {
  const { uuid } = useParams({ from: '/share/geometry/$uuid' });
  const { isAuthenticated } = useAuth();
  useNoIndex();

  const isValidUuid = UUID_REGEX.test(uuid);

  const {
    data: geometry,
    isLoading,
    isError,
    error,
  } = useQuery({
    ...handleGetGeometryGeometriesGeometryIdGetOptions({
      path: { geometry_id: uuid },
    }),
    retry: false,
    enabled: isValidUuid,
  });

  useRecordRecentView({
    kind: 'geometry',
    id: isValidUuid ? uuid : null,
    name: geometry?.filename,
    myRole: geometry?.my_role,
  });

  if (!isValidUuid) {
    return <AccessDeniedScreen />;
  }

  if (isLoading) {
    return <CenteredSpinner />;
  }

  if (isError) {
    return isAccessDeniedError(error) ? (
      <AccessDeniedScreen />
    ) : (
      <ShareLoadErrorScreen />
    );
  }

  if (!geometry) {
    return <AccessDeniedScreen />;
  }

  const myRole = geometry.my_role;
  const showOpenInApp = canOpenInApp(isAuthenticated, myRole);
  const bannerRole = readOnlyBannerRole(isAuthenticated, myRole);

  return (
    <div className="flex h-screen flex-col bg-background">
      {showOpenInApp && <OpenGeometryInAppBanner uuid={uuid} />}
      {bannerRole && <ReadOnlyBanner role={bannerRole} ownerEmail={null} />}
      <div className="min-h-0 flex-1">
        <WorkspaceReadOnlyProvider value={true}>
          <MeshGenWorkspace projectId={uuid} showLibraryBack={false} />
        </WorkspaceReadOnlyProvider>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/share/geometry/$uuid')({
  validateSearch: shareGeometrySearchSchema,
  component: GeometryShareRoute,
});
